import { useState, useMemo } from "react";
import { MENU, MENU_CATEGORY_NOTES, MEDIA, CATEGORY_IMAGES } from "../lib/bakeryData";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

// Group categories into themed sections for the tab bar
const SECTIONS = {
    "Sweets": [
        "Cakes",
        "Pastries",
        "Cheese Cake Slice",
        "Desserts",
        "Cupcakes",
        "Muffins",
        "Donuts",
        "Croissants",
    ],
    "Tea Time": [
        "Dry Cakes",
        "Healthy Dry Cakes",
        "Premium Cookies",
        "Healthy Cookies",
        "Khari",
        "Rusk & Toasts",
    ],
    "Breads": ["Handcrafted Breads"],
    "Custom Cakes": ["Custom Cakes (Normal)", "Custom Cakes (Premium)"],
    "Café": [
        "Café — Quick Hot Bites",
        "Café — Hot Beverages",
        "Café — Thick Shakes (250ml)",
    ],
};

// Map a category to a representative hero image
const sectionImages = {
    "Sweets": MEDIA.belgianCake,
    "Tea Time": MEDIA.attaChocoAlmond,
    "Breads": MEDIA.whiteBread,
    "Custom Cakes": MEDIA.weddingCake,
    "Café": MEDIA.belgianDarkShake,
};

const Menu = () => {
    const sections = useMemo(() => Object.keys(SECTIONS), []);
    const [active, setActive] = useState(sections[0]);

    return (
        <main data-testid="menu-page" className="bg-[#FAF8F5] pt-32 pb-20">
            <section className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="text-center max-w-3xl mx-auto">
                    <p className="font-handwriting text-3xl text-[#C5832B]">
                        Today's bake
                    </p>
                    <h1 className="font-serif text-5xl md:text-6xl text-[#3E2723] mt-3 leading-tight">
                        The Menu
                    </h1>
                    <p className="text-base text-[#6D4C41] mt-5 leading-relaxed">
                        A small, ever-changing list — driven by what's seasonal, what's
                        sold out, and what we couldn't resist baking this morning. Prices
                        and availability may vary by branch.
                    </p>
                </div>

                <Tabs
                    value={active}
                    onValueChange={setActive}
                    className="mt-14"
                    data-testid="menu-tabs"
                >
                    <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent h-auto p-0">
                        {sections.map((sec) => (
                            <TabsTrigger
                                key={sec}
                                value={sec}
                                data-testid={`menu-tab-${sec.toLowerCase().replace(/\s+/g, "-")}`}
                                className="rounded-full px-6 py-2.5 text-sm uppercase tracking-wide border border-[#E8D8C8] bg-white text-[#3E2723] data-[state=active]:bg-[#3E2723] data-[state=active]:text-[#FAF8F5] data-[state=active]:border-[#3E2723] hover:border-[#C5832B] transition-colors"
                            >
                                {sec}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {sections.map((sec) => (
                        <TabsContent key={sec} value={sec} className="mt-12">
                            <div className="grid lg:grid-cols-12 gap-10">
                                {/* Hero image rail */}
                                <div className="lg:col-span-4 fade-up">
                                    <div
                                        className="sticky top-28 aspect-[4/5] rounded-3xl overflow-hidden border border-[#E8D8C8]"
                                        style={{ background: sec === "Custom Cakes" ? "#1a0f0d" : "transparent" }}
                                    >
                                        <img
                                            src={sectionImages[sec]}
                                            alt={sec}
                                            className={`w-full h-full ${sec === "Custom Cakes" ? "object-contain" : "object-cover"}`}
                                        />
                                    </div>
                                    <p className="font-handwriting text-3xl text-[#C5832B] mt-6">
                                        {sec}
                                    </p>
                                    <p className="text-sm text-[#6D4C41] mt-2 leading-relaxed">
                                        {sec === "Sweets" && "Cakes, pastries, cheesecake slices, donuts, muffins, cupcakes and croissants — our daily counter, all in one place."}
                                        {sec === "Tea Time" && "Cookies, khari, rusk and dry cakes — the little crunches that pair perfectly with your evening chai."}
                                        {sec === "Breads" && "Hand-shaped breads, baked fresh every morning at every branch."}
                                        {sec === "Custom Cakes" && "Made-to-order cakes for birthdays, weddings and Tuesdays. Please place orders 24–72 hours in advance."}
                                        {sec === "Café" && "Quick hot bites, espresso-bar classics, and thick shakes — best enjoyed in-store."}
                                    </p>
                                </div>

                                {/* Categories + items */}
                                <div className="lg:col-span-8 space-y-14">
                                    {SECTIONS[sec].map((cat) => (
                                        <div
                                            key={cat}
                                            data-testid={`menu-category-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                                        >
                                            {CATEGORY_IMAGES[cat] && (() => {
                                                const entry = CATEGORY_IMAGES[cat];
                                                const src = typeof entry === "string" ? entry : entry.src;
                                                const fit = typeof entry === "string" ? "cover" : (entry.fit || "cover");
                                                const bg = typeof entry === "string" ? "transparent" : (entry.bg || "transparent");
                                                return (
                                                    <div
                                                        className="relative aspect-[16/7] rounded-2xl overflow-hidden border border-[#E8D8C8] mb-5"
                                                        style={{ background: bg }}
                                                    >
                                                        <img
                                                            src={src}
                                                            alt={cat}
                                                            loading="lazy"
                                                            className={`w-full h-full ${fit === "contain" ? "object-contain" : "object-cover"}`}
                                                        />
                                                        {fit !== "contain" && (
                                                            <div className="absolute inset-0 bg-gradient-to-t from-[#2A1A17]/55 via-transparent to-transparent" />
                                                        )}
                                                        <p className={`absolute bottom-4 left-5 font-handwriting text-2xl drop-shadow ${fit === "contain" ? "text-[#C5832B]" : "text-[#FAF8F5]"}`}>
                                                            {cat}
                                                        </p>
                                                    </div>
                                                );
                                            })()}
                                            <div className="flex items-baseline justify-between gap-4 mb-5 pb-3 border-b border-[#E8D8C8]">
                                                <h2 className="font-serif text-3xl text-[#3E2723]">
                                                    {cat}
                                                </h2>
                                                <span className="font-handwriting text-xl text-[#C5832B] hidden md:inline">
                                                    {MENU[cat]?.length} items
                                                </span>
                                            </div>
                                            {MENU_CATEGORY_NOTES[cat] && (
                                                <p className="text-sm text-[#6D4C41] mb-5 leading-relaxed">
                                                    {MENU_CATEGORY_NOTES[cat]}
                                                </p>
                                            )}
                                            <ul className="divide-y divide-[#E8D8C8]/70">
                                                {MENU[cat]?.map((item, i) => (
                                                    <li
                                                        key={`${cat}-${item.name}`}
                                                        data-testid={`menu-item-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-${i}`}
                                                        className="py-4 flex items-center justify-between gap-6 hover:bg-[#F3EBE1]/40 px-3 -mx-3 rounded-xl transition-colors"
                                                    >
                                                        <div className="flex-1">
                                                            <h3 className="font-serif text-xl text-[#3E2723] leading-tight">
                                                                {item.name}
                                                            </h3>
                                                            {item.desc && (
                                                                <p className="text-sm text-[#6D4C41] mt-1 leading-relaxed max-w-lg">
                                                                    {item.desc}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <p className="font-handwriting text-2xl md:text-3xl text-[#C5832B] whitespace-nowrap">
                                                            {item.price}
                                                        </p>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </TabsContent>
                    ))}
                </Tabs>

                <div className="mt-20 rounded-[2rem] bg-[#F3EBE1] border border-[#E8D8C8] p-10 text-center">
                    <p className="font-handwriting text-3xl text-[#C5832B]">
                        Can't visit us in person?
                    </p>
                    <h3 className="font-serif text-3xl md:text-4xl text-[#3E2723] mt-2">
                        Order on WhatsApp, Swiggy or Zomato
                    </h3>
                    <p className="text-sm text-[#6D4C41] mt-3 max-w-xl mx-auto">
                        Pick the branch closest to you, and your basket is on its way.
                    </p>
                    <a
                        href="/branches"
                        data-testid="menu-order-cta"
                        className="inline-flex items-center gap-2 mt-7 px-7 py-3.5 rounded-full bg-[#3E2723] text-[#FAF8F5] text-sm hover:bg-[#C5832B] transition-colors"
                    >
                        Choose a branch
                    </a>
                </div>
            </section>
        </main>
    );
};

export default Menu;
